/* =========================================================================
 * PLACE THE REAL MARK ONTO A RESERVED PLATE.
 *
 * ⚠️ WHAT MAKES THIS RUNG 2 RATHER THAN RUNG 1. The mark drawn here is the
 * school's OWNED FILE, pixel for pixel, scaled and warped onto the plate. It
 * is not the model's approximation of their mark. On a rung 1 sheet the model
 * redraws the crest and gets it subtly wrong every time: measured 2026-09-18 on
 * a two-colour test mark, it inverted figure and ground, turning a blue ring
 * with a bar into a filled blue disc with a white bar. Placing the real file
 * cannot do that, because nothing regenerates it.
 *
 * ⚠️ THE HOUSING IS STILL DRAWN. That is the whole reason this is rung 2 and
 * not a proof, and it is why `markSource` stays
 * `owned-crest-on-generated-housing` and never coerces upward.
 *
 * PERSPECTIVE, NOT A PASTE. A bezel face is photographed at a slight angle, so
 * its plate is a trapezium. Canvas 2D has no perspective transform, so the mark
 * is drawn as TWO AFFINE TRIANGLES that share the quad's diagonal, which is the
 * standard decomposition and is exact at the corners.
 *
 * THE PLATE'S OWN SHADING IS KEPT. A flat paste reads as a sticker. The mark is
 * composited with `multiply` against the plate underneath so the metal's
 * gradient and its specular roll-off come through the artwork, then the whole
 * thing is clipped to the quad so nothing bleeds onto the stones.
 * ========================================================================= */

/**
 * Draw one affine triangle of `img` into the destination triangle.
 *
 * ⚠️ THIS PRODUCED NOTHING AT ALL, AND THE PANEL SAID IT HAD WORKED.
 *
 * Found 2026-09-19 by driving the real component: "Plates found: 21 of 21",
 * and an output sheet with not one pixel of the mark on it. The previous
 * implementation wrote the six transform terms out by hand and the constant
 * `f` was negated where `e` was not. Solve the simplest case — a square mark
 * onto an axis-aligned plate, src (0,0) → dst (114,114) — and it yields
 * e = +114 with f = -114, so every mark was translated clean off the top of
 * the canvas. Silently: an empty layer multiplied over the sheet leaves the
 * sheet exactly as it was, which is why the result looked like a plausible
 * untouched sheet rather than an error.
 *
 * It is rewritten as a straight 3-point solve rather than a sign fix, because
 * six hand-expanded terms are how the error got in and are unreadable when
 * checking for the next one. Each coefficient now comes from Cramer's rule on
 * the same determinant, and `u` (x) and `v` (y) are computed by the SAME
 * expression, so an asymmetry between them is visible on sight.
 *
 *   u = a·x + c·y + e        v = b·x + d·y + f
 */
function solveAffine(x0, y0, x1, y1, x2, y2, u0, u1, u2, den) {
  return [
    (u0 * (y1 - y2) + u1 * (y2 - y0) + u2 * (y0 - y1)) / den,               // x coefficient
    (x0 * (u1 - u2) + x1 * (u2 - u0) + x2 * (u0 - u1)) / den,               // y coefficient
    (x0 * (y1 * u2 - y2 * u1) + x1 * (y2 * u0 - y0 * u2) + x2 * (y0 * u1 - y1 * u0)) / den, // constant
  ]
}

/**
 * The six canvas transform terms mapping source triangle `s` onto dest `d`,
 * or null when the source is degenerate.
 *
 * EXPORTED SO IT CAN BE TESTED WITHOUT A BROWSER. The bug above shipped
 * because the only way to observe this maths was to composite an image and
 * look at it, and nothing in the build does that. As a pure function it is
 * checked by verify-composite.mjs in plain Node, and the test fails the build.
 */
export function affineFor(s, d) {
  const [[sx0, sy0], [sx1, sy1], [sx2, sy2]] = s
  const [[dx0, dy0], [dx1, dy1], [dx2, dy2]] = d
  const den = sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1)
  if (!den) return null
  const [a, c, e] = solveAffine(sx0, sy0, sx1, sy1, sx2, sy2, dx0, dx1, dx2, den)
  const [b, d2, f] = solveAffine(sx0, sy0, sx1, sy1, sx2, sy2, dy0, dy1, dy2, den)
  return [a, b, c, d2, e, f]
}

/** Apply an affine map to a point, in the same order canvas does. */
export function applyAffine([a, b, c, d, e, f], [x, y]) {
  return [a * x + c * y + e, b * x + d * y + f]
}

function triangle(ctx, img, s, d) {
  const m = affineFor(s, d)
  if (!m) return                          // degenerate triangle: nothing to draw
  const [a, b, c, d2, e, f] = m
  const [[dx0, dy0], [dx1, dy1], [dx2, dy2]] = d

  ctx.save()
  ctx.beginPath()
  ctx.moveTo(dx0, dy0); ctx.lineTo(dx1, dy1); ctx.lineTo(dx2, dy2)
  ctx.closePath()
  ctx.clip()
  ctx.transform(a, b, c, d2, e, f)
  ctx.drawImage(img, 0, 0)
  ctx.restore()
}

/**
 * Composite `mark` onto `housing` at `quad`, returning a canvas.
 *
 * `inset` keeps the artwork off the machined edge of the pad; a mark that
 * touches the bezel wall reads as a misprint rather than as engraving.
 */
export function placeMarkOnQuad(housing, mark, quad, { inset = 0.86, shade = 0.75 } = {}) {
  const canvas = document.createElement('canvas')
  canvas.width = housing.naturalWidth || housing.width
  canvas.height = housing.naturalHeight || housing.height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(housing, 0, 0)

  const pts = ['tl', 'tr', 'br', 'bl'].map((k) => quad[k])
  const cx = pts.reduce((s, p) => s + p[0], 0) / 4
  const cy = pts.reduce((s, p) => s + p[1], 0) / 4
  const inner = pts.map(([x, y]) => [cx + (x - cx) * inset, cy + (y - cy) * inset])

  /* Fit the mark inside the quad preserving its aspect: a squashed crest is a
   * worse failure than a smaller one. */
  const qw = (Math.hypot(inner[1][0] - inner[0][0], inner[1][1] - inner[0][1])
    + Math.hypot(inner[2][0] - inner[3][0], inner[2][1] - inner[3][1])) / 2
  const qh = (Math.hypot(inner[3][0] - inner[0][0], inner[3][1] - inner[0][1])
    + Math.hypot(inner[2][0] - inner[1][0], inner[2][1] - inner[1][1])) / 2
  const mw = mark.naturalWidth || mark.width
  const mh = mark.naturalHeight || mark.height
  const scale = Math.min(qw / mw, qh / mh)
  const fw = mw * scale
  const fh = mh * scale

  /* Centre the fitted mark inside the quad by shrinking the quad to the
   * mark's own aspect, so the warp maps the artwork rather than a letterbox. */
  const kx = fw / qw
  const ky = fh / qh
  const lerp = (p, q, t) => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]
  const tx0 = (1 - kx) / 2, tx1 = 1 - tx0
  const ty0 = (1 - ky) / 2, ty1 = 1 - ty0
  const top = [lerp(inner[0], inner[1], tx0), lerp(inner[0], inner[1], tx1)]
  const bot = [lerp(inner[3], inner[2], tx0), lerp(inner[3], inner[2], tx1)]
  const dst = [
    lerp(top[0], bot[0], ty0), lerp(top[1], bot[1], ty0),
    lerp(top[1], bot[1], ty1), lerp(top[0], bot[0], ty1),
  ]

  const layer = document.createElement('canvas')
  layer.width = canvas.width
  layer.height = canvas.height
  const lx = layer.getContext('2d')
  const src = [[0, 0], [mw, 0], [mw, mh], [0, mh]]
  triangle(lx, mark, [src[0], src[1], src[2]], [dst[0], dst[1], dst[2]])
  triangle(lx, mark, [src[0], src[2], src[3]], [dst[0], dst[2], dst[3]])

  /* The mark sits IN the metal, not on it: multiply carries the plate's own
   * gradient through the artwork. Blended back at `shade` so a dark plate does
   * not swallow a dark mark entirely. */
  ctx.save()
  ctx.globalAlpha = shade
  ctx.globalCompositeOperation = 'multiply'
  ctx.drawImage(layer, 0, 0)
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = 1 - shade
  ctx.drawImage(layer, 0, 0)
  ctx.restore()

  return canvas
}

/** Load an image, cross-origin enabled so an owned File Manager URL can be read. */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`could not load ${String(src).slice(0, 80)}`))
    img.src = src
  })
}

/** A canvas as a PNG blob, for the owned-asset upload. */
export function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('the composite could not be encoded'))), 'image/png')
  })
}
